import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, LogIn, LogOut } from 'lucide-react';
import type { CertificationCohort } from '@/lib/api/services/certification.service';

interface CohortCardProps {
  cohort: CertificationCohort;
  isMember?: boolean;
  isOrganizer?: boolean;
  onJoin?: () => void;
  onLeave?: () => void;
  onViewDetail?: () => void;
  isJoining?: boolean;
  isLeaving?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  forming: 'bg-blue-100 text-blue-700',
  full: 'bg-amber-100 text-amber-700',
  submitted: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
};

export function CohortCard({
  cohort,
  isMember,
  isOrganizer,
  onJoin,
  onLeave,
  onViewDetail,
  isJoining,
  isLeaving,
}: CohortCardProps) {
  const memberCount = cohort.certification_cohort_members?.[0]?.count || 0;
  const progress = (memberCount / cohort.max_members) * 100;
  const organizerName = cohort.freelancer_profiles?.display_name || 'Unknown';
  const organizerPic = cohort.freelancer_profiles?.profile_picture_url;
  const organizerInitials = organizerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onViewDetail}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm truncate">{cohort.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[cohort.status] || ''}`}>
                {cohort.status}
              </span>
            </div>
            {cohort.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{cohort.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <Avatar className="h-6 w-6">
            <AvatarImage src={organizerPic || undefined} />
            <AvatarFallback className="text-[10px] bg-color-1/10 text-color-1">{organizerInitials}</AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground">Organized by {organizerName}</span>
        </div>

        <div className="mt-4 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-3 w-3" />
              Members
            </span>
            <span className="font-semibold">{memberCount}/{cohort.max_members}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="mt-4 flex gap-2" onClick={(e) => e.stopPropagation()}>
          {!isMember && cohort.status === 'forming' && onJoin && (
            <Button
              size="sm"
              className="flex-1 text-xs"
              onClick={onJoin}
              disabled={isJoining}
            >
              <LogIn className="h-3 w-3 mr-1" />
              {isJoining ? 'Joining...' : 'Join Cohort'}
            </Button>
          )}
          {isMember && !isOrganizer && onLeave && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={onLeave}
              disabled={isLeaving}
            >
              <LogOut className="h-3 w-3 mr-1" />
              {isLeaving ? 'Leaving...' : 'Leave'}
            </Button>
          )}
          {isMember && (
            <Badge variant="secondary" className="text-[10px]">
              {isOrganizer ? 'Organizer' : 'Member'}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
